import { useIsMobile } from "@/hooks/use-mobile";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
  toYMD,
} from "@/lib/utils";
import { useUpdatePdcMutationFinance } from "@/services/school/commercial/installment/useUpdatePdcMutationFinance";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  IS_PAYMENT_RECEIVED,
  PAID_AMOUNT,
  PAID_DATE,
  PAYMENT_RECEIVED_ON,
  PAYMENT_REMARKS,
  PAYMENT_STATUS,
} from "./consts";
import { formValues, schema } from "./validations";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdatePaymentMutation } from "@/services/school/commercial/installment/payment/useUpdatePaymentMutation";

interface UseUpdateAmountProps {
  onCancel: () => void;
  editData?: any;
}

const useUpdatePayment = ({ onCancel, editData }: UseUpdateAmountProps) => {
  const isMobile = useIsMobile();
  const { action, id, installmentIndex } = useParams();
  const queryClient = useQueryClient();
  const invoiceId = queryClient.getQueryData<string>(["current-invoice-id"]);

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [PAID_AMOUNT]: "",
      [PAID_DATE]: "",
      [PAYMENT_STATUS]: "",
      [IS_PAYMENT_RECEIVED]: false,
      [PAYMENT_RECEIVED_ON]: "",
      [PAYMENT_REMARKS]: "",
    },
  });

  const requiredFields = getRequiredFields(schema);
  const updatePayment = useUpdatePaymentMutation({
    onSuccess: () => {
      // Invalidate with correct query key matching the actual query
      queryClient.invalidateQueries({
        queryKey: ["payment", installmentIndex],
      });
      // Also invalidate any other payment-related queries
      queryClient.invalidateQueries({
        queryKey: ["payment"],
      });
      onCancel();
    },
  });

  useEffect(() => {
    if (editData) {
      form.reset({
        [PAID_AMOUNT]: String(editData.paidAmount) || "",
        [PAID_DATE]: editData.paidDate || "",
        [PAYMENT_STATUS]: editData.paymentStatus || "",
        [IS_PAYMENT_RECEIVED]: editData.isPaymentReceivedByCS || false,
        [PAYMENT_RECEIVED_ON]: editData.paymentReceivedOn || "",
        [PAYMENT_REMARKS]: editData.paymentRemarks || "",
      });
    } else {
      form.reset({
        [PAID_AMOUNT]: "",
        [PAID_DATE]: "",
        [PAYMENT_STATUS]: "",
        [IS_PAYMENT_RECEIVED]: false,
        [PAYMENT_RECEIVED_ON]: "",
        [PAYMENT_REMARKS]: "",
      });
    }
  }, [editData, form]);

  const onSubmit = (data: formValues) => {
    let _data = setEmptyFieldsToNull(data);
    // _data.invoiceId = String(invoiceId);
    _data.paymentReceivedOn = _data.paymentReceivedOn
      ? toYMD(_data?.paymentReceivedOn)
      : null;
    _data.paidDate = _data.paidDate ? toYMD(_data?.paidDate) : null;

    // Add invoicePaymentId for edit mode
    if (editData?.invoicePaymentId) {
      _data.invoicePaymentId = editData.invoicePaymentId;
    }

   invoiceId && (_data.invoiceId = String(invoiceId));
    _data = removeEmptyFields(_data);
    updatePayment.mutate(_data as any);
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isMobile,
  };
};

export default useUpdatePayment;
